"use client";

import type { ReactNode, RefObject } from "react";
import {
  clamp,
  DiagramFrame,
  fitBox,
  Guide,
  Label,
  Outline,
  RightAngle,
  SHAPE,
} from "@/components/tools/geometry/diagram";
import type { AreaShapeId, LengthFieldId } from "@/lib/tool-kit/geometry/area";

/*
 * The labelled figure under each area result. Proportions follow the numbers typed
 * (kept within a readable range), so a long thin rectangle looks long and thin.
 */

/** The letter each measurement goes by in the formulas. */
export const FIELD_SYMBOLS: Record<LengthFieldId, string> = {
  side: "s",
  length: "l",
  width: "w",
  base: "b",
  height: "h",
  sideA: "a",
  sideB: "b",
  sideC: "c",
  base1: "a",
  base2: "b",
  diag1: "d₁",
  diag2: "d₂",
  semiA: "a",
  semiB: "b",
  outer: "R",
  inner: "r",
  radius: "r",
};

export interface AreaDiagramProps {
  shape: AreaShapeId;
  title: string;
  svgRef: RefObject<SVGSVGElement | null>;
  /** Each measurement with its unit, as typed: "6 cm". */
  labels: Partial<Record<LengthFieldId, string>>;
  /** The same measurements as numbers in one unit, for proportions. */
  values: Partial<Record<LengthFieldId, number>>;
  sides: number;
  angleDegrees: number;
  /** "n = 5" and "θ = 60°". */
  sidesLabel: string;
  angleLabel: string;
}

interface DrawProps {
  label: (id: LengthFieldId) => string;
  value: (id: LengthFieldId) => number;
  sides: number;
  angleDegrees: number;
  sidesLabel: string;
  angleLabel: string;
}

const CX = 140;
const BOTTOM = 185;
const MAX_W = 200;
const MAX_H = 150;
const RADIUS = 80;
const CY = 108;

function Square({ label }: DrawProps) {
  const size = 140;
  const x0 = CX - size / 2;
  const y0 = BOTTOM - size;
  return (
    <>
      <Outline d={`M${x0} ${y0} h${size} v${size} h${-size} Z`} />
      <Label x={CX} y={BOTTOM + 20}>
        {label("side")}
      </Label>
      <Label anchor="start" x={x0 + size + 8} y={y0 + size / 2 + 4}>
        {label("side")}
      </Label>
    </>
  );
}

function Rectangle({ label, value }: DrawProps) {
  const { w, h } = fitBox(value("length") / value("width"), MAX_W, MAX_H);
  const x0 = CX - w / 2;
  const y0 = BOTTOM - h;
  return (
    <>
      <Outline d={`M${x0} ${y0} h${w} v${h} h${-w} Z`} />
      <Label x={CX} y={BOTTOM + 20}>
        {label("length")}
      </Label>
      <Label anchor="start" x={x0 + w + 8} y={y0 + h / 2 + 4}>
        {label("width")}
      </Label>
    </>
  );
}

function Triangle({ label, value }: DrawProps) {
  const { w, h } = fitBox(value("base") / value("height"), MAX_W, MAX_H);
  const x0 = CX - w / 2;
  const apexX = x0 + w * 0.3;
  const top = BOTTOM - h;
  return (
    <>
      <Outline d={`M${x0} ${BOTTOM} L${x0 + w} ${BOTTOM} L${apexX} ${top} Z`} />
      <Guide x1={apexX} x2={apexX} y1={top} y2={BOTTOM} />
      <RightAngle dx={1} dy={-1} x={apexX} y={BOTTOM} />
      <Label x={CX} y={BOTTOM + 20}>
        {label("base")}
      </Label>
      <Label
        anchor="start"
        color={SHAPE.guide}
        x={apexX + 8}
        y={(top + BOTTOM) / 2 + 4}
      >
        {label("height")}
      </Label>
    </>
  );
}

interface Point {
  x: number;
  y: number;
}

function sideLabelPosition(from: Point, to: Point, centre: Point) {
  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  const dx = mid.x - centre.x;
  const dy = mid.y - centre.y;
  const length = Math.hypot(dx, dy) || 1;
  const anchor = dx / length > 0.3 ? "start" : "middle";
  const x = mid.x + (dx / length) * 10;
  return {
    x: dx / length < -0.3 ? x - 4 : x,
    y: mid.y + (dy / length) * 14 + 4,
    anchor:
      dx / length < -0.3 ? ("end" as const) : (anchor as "start" | "middle"),
  };
}

function Triangle3({ label, value }: DrawProps) {
  const a = value("sideA");
  const b = value("sideB");
  const c = value("sideC");
  const along = (b * b + c * c - a * a) / (2 * c);
  const rise = Math.sqrt(Math.max(b * b - along * along, 0));
  const minX = Math.min(0, along);
  const maxX = Math.max(c, along);
  const scale = Math.min(MAX_W / (maxX - minX || 1), MAX_H / (rise || 1));
  const left = CX - ((maxX - minX) * scale) / 2;
  const at = (x: number, y: number): Point => ({
    x: left + (x - minX) * scale,
    y: BOTTOM - y * scale,
  });
  const pa = at(0, 0);
  const pb = at(c, 0);
  const pc = at(along, rise);
  const centre = {
    x: (pa.x + pb.x + pc.x) / 3,
    y: (pa.y + pb.y + pc.y) / 3,
  };
  const labelA = sideLabelPosition(pb, pc, centre);
  const labelB = sideLabelPosition(pc, pa, centre);
  return (
    <>
      <Outline d={`M${pa.x} ${pa.y} L${pb.x} ${pb.y} L${pc.x} ${pc.y} Z`} />
      <Label x={(pa.x + pb.x) / 2} y={BOTTOM + 20}>
        {label("sideC")}
      </Label>
      <Label anchor={labelA.anchor} x={labelA.x} y={labelA.y}>
        {label("sideA")}
      </Label>
      <Label anchor={labelB.anchor} x={labelB.x} y={labelB.y}>
        {label("sideB")}
      </Label>
    </>
  );
}

function Parallelogram({ label, value }: DrawProps) {
  const { w, h } = fitBox(
    (value("base") * 1.3) / value("height"),
    MAX_W,
    MAX_H
  );
  const base = w / 1.3;
  const slant = base * 0.3;
  const x0 = CX - w / 2;
  const top = BOTTOM - h;
  const footX = x0 + slant;
  return (
    <>
      <Outline
        d={`M${x0} ${BOTTOM} L${x0 + base} ${BOTTOM} L${x0 + base + slant} ${top} L${footX} ${top} Z`}
      />
      <Guide x1={footX} x2={footX} y1={top} y2={BOTTOM} />
      <RightAngle dx={1} dy={-1} x={footX} y={BOTTOM} />
      <Label x={x0 + base / 2} y={BOTTOM + 20}>
        {label("base")}
      </Label>
      <Label
        anchor="start"
        color={SHAPE.guide}
        x={footX + 8}
        y={(top + BOTTOM) / 2 + 4}
      >
        {label("height")}
      </Label>
    </>
  );
}

function Trapezoid({ label, value }: DrawProps) {
  const a = value("base1");
  const b = value("base2");
  const widest = Math.max(a, b);
  const { w, h } = fitBox(widest / value("height"), MAX_W, MAX_H);
  const k = w / widest;
  const top = BOTTOM - h;
  const topHalf = (a * k) / 2;
  const bottomHalf = (b * k) / 2;
  return (
    <>
      <Outline
        d={`M${CX - bottomHalf} ${BOTTOM} L${CX + bottomHalf} ${BOTTOM} L${CX + topHalf} ${top} L${CX - topHalf} ${top} Z`}
      />
      <Guide x1={CX} x2={CX} y1={top} y2={BOTTOM} />
      <RightAngle dx={1} dy={-1} x={CX} y={BOTTOM} />
      <Label x={CX} y={top - 8}>
        {label("base1")}
      </Label>
      <Label x={CX} y={BOTTOM + 20}>
        {label("base2")}
      </Label>
      <Label
        anchor="start"
        color={SHAPE.guide}
        x={CX + 8}
        y={(top + BOTTOM) / 2 + 4}
      >
        {label("height")}
      </Label>
    </>
  );
}

function Rhombus({ label, value }: DrawProps) {
  const { w, h } = fitBox(value("diag1") / value("diag2"), MAX_W, MAX_H - 20);
  const cy = CY + 4;
  return (
    <>
      <Outline
        d={`M${CX - w / 2} ${cy} L${CX} ${cy - h / 2} L${CX + w / 2} ${cy} L${CX} ${cy + h / 2} Z`}
      />
      <Guide x1={CX - w / 2} x2={CX + w / 2} y1={cy} y2={cy} />
      <Guide x1={CX} x2={CX} y1={cy - h / 2} y2={cy + h / 2} />
      <RightAngle dx={1} dy={-1} size={8} x={CX} y={cy} />
      <Label anchor="start" x={CX + w / 2 + 8} y={cy + 4}>
        {label("diag1")}
      </Label>
      <Label anchor="start" x={CX + 8} y={cy - h / 2 - 6}>
        {label("diag2")}
      </Label>
    </>
  );
}

function Polygon({ label, sides, sidesLabel }: DrawProps) {
  const n = Math.max(3, Math.round(sides));
  const R = RADIUS + 6;
  const cy = 100;
  const vertex = (k: number): Point => {
    const phi = ((270 - 180 / n + (360 * k) / n) * Math.PI) / 180;
    return { x: CX + R * Math.cos(phi), y: cy - R * Math.sin(phi) };
  };
  const points = Array.from({ length: n }, (_, k) => vertex(k));
  const path = `${points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ")} Z`;
  const apothem = R * Math.cos(Math.PI / n);
  return (
    <>
      <Outline d={path} />
      <Guide x1={CX} x2={CX} y1={cy} y2={cy + apothem} />
      <Label x={CX} y={cy + apothem + 20}>
        {label("side")}
      </Label>
      <Label anchor="start" x={CX + R + 14} y={cy + 4}>
        {sidesLabel}
      </Label>
    </>
  );
}

function Ellipse({ label, value }: DrawProps) {
  const { w, h } = fitBox(value("semiA") / value("semiB"), MAX_W, MAX_H);
  const rx = w / 2;
  const ry = h / 2;
  return (
    <>
      <ellipse
        cx={CX}
        cy={CY}
        fill={SHAPE.fill}
        fillOpacity={SHAPE.fillOpacity}
        rx={rx}
        ry={ry}
        stroke={SHAPE.stroke}
        strokeWidth={2.5}
      />
      <Guide x1={CX} x2={CX + rx} y1={CY} y2={CY} />
      <Guide x1={CX} x2={CX} y1={CY} y2={CY - ry} />
      <Label x={CX + rx / 2} y={CY + 18}>
        {label("semiA")}
      </Label>
      <Label anchor="end" x={CX - 8} y={CY - ry / 2 + 4}>
        {label("semiB")}
      </Label>
    </>
  );
}

function Annulus({ label, value }: DrawProps) {
  const inner = clamp(
    (value("inner") / value("outer")) * RADIUS,
    6,
    RADIUS - 6
  );
  const roomy = inner >= 62;
  return (
    <>
      <circle
        cx={CX}
        cy={CY}
        fill={SHAPE.fill}
        fillOpacity={SHAPE.fillOpacity}
        r={RADIUS}
        stroke={SHAPE.stroke}
        strokeWidth={2.5}
      />
      <circle
        cx={CX}
        cy={CY}
        fill="#ffffff"
        r={inner}
        stroke={SHAPE.stroke}
        strokeWidth={2.5}
      />
      <Guide x1={CX} x2={CX + RADIUS} y1={CY} y2={CY} />
      <Guide x1={CX} x2={CX - inner} y1={CY} y2={CY} />
      <Label anchor="start" x={CX + RADIUS + 8} y={CY + 4}>
        {label("outer")}
      </Label>
      {roomy ? (
        <Label x={CX - inner / 2} y={CY - 8}>
          {label("inner")}
        </Label>
      ) : (
        <Label anchor="end" x={CX - RADIUS - 8} y={CY + 4}>
          {label("inner")}
        </Label>
      )}
    </>
  );
}

function Sector({ label, angleDegrees, angleLabel }: DrawProps) {
  const theta = clamp(angleDegrees, 0.5, 360);
  const rad = (theta * Math.PI) / 180;
  const end = {
    x: CX + RADIUS * Math.cos(rad),
    y: CY - RADIUS * Math.sin(rad),
  };
  const big = theta > 180 ? 1 : 0;
  const wedge =
    theta >= 359.99
      ? `M${CX} ${CY} m${RADIUS} 0 a${RADIUS} ${RADIUS} 0 1 0 ${-2 * RADIUS} 0 a${RADIUS} ${RADIUS} 0 1 0 ${2 * RADIUS} 0`
      : `M${CX} ${CY} L${CX + RADIUS} ${CY} A${RADIUS} ${RADIUS} 0 ${big} 0 ${end.x} ${end.y} Z`;
  const small = 26;
  const arcEnd = {
    x: CX + small * Math.cos(rad),
    y: CY - small * Math.sin(rad),
  };
  const mid = rad / 2;
  return (
    <>
      <circle
        cx={CX}
        cy={CY}
        fill="none"
        r={RADIUS}
        stroke={SHAPE.muted}
        strokeDasharray="3 4"
        strokeWidth={1}
      />
      <Outline d={wedge} />
      {theta >= 359.99 ? null : (
        <path
          d={`M${CX + small} ${CY} A${small} ${small} 0 ${big} 0 ${arcEnd.x} ${arcEnd.y}`}
          fill="none"
          stroke={SHAPE.guide}
          strokeWidth={1.5}
        />
      )}
      <Label x={CX + RADIUS / 2} y={CY + 18}>
        {label("radius")}
      </Label>
      <Label
        anchor={Math.cos(mid) >= 0 ? "start" : "end"}
        color={SHAPE.guide}
        x={CX + (RADIUS + 10) * Math.cos(mid)}
        y={CY - (RADIUS + 10) * Math.sin(mid) + 4}
      >
        {angleLabel}
      </Label>
    </>
  );
}

const DRAWINGS: Record<AreaShapeId, (props: DrawProps) => ReactNode> = {
  square: Square,
  rectangle: Rectangle,
  triangle: Triangle,
  triangle3: Triangle3,
  parallelogram: Parallelogram,
  trapezoid: Trapezoid,
  rhombus: Rhombus,
  polygon: Polygon,
  ellipse: Ellipse,
  annulus: Annulus,
  sector: Sector,
};

export function AreaDiagram(props: AreaDiagramProps) {
  const Draw = DRAWINGS[props.shape];
  const draw: DrawProps = {
    label: (id) => `${FIELD_SYMBOLS[id]} = ${props.labels[id] ?? ""}`,
    value: (id) => props.values[id] ?? 1,
    sides: props.sides,
    angleDegrees: props.angleDegrees,
    sidesLabel: props.sidesLabel,
    angleLabel: props.angleLabel,
  };
  return (
    <DiagramFrame svgRef={props.svgRef} title={props.title}>
      <Draw {...draw} />
    </DiagramFrame>
  );
}
